import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import apiClient from '@/lib/apiClient';

export default function MonitoringTab({ isDark, userInfo, selectedMunicipalityId, onOpenInTestumgebung }) {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [intentBreakdown, setIntentBreakdown] = useState([]);
  const [intentBreakdownLoading, setIntentBreakdownLoading] = useState(false);
  const [intentBreakdownError, setIntentBreakdownError] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [recentLogsLoading, setRecentLogsLoading] = useState(false);
  const [gapsList, setGapsList] = useState([]);
  const [gapsLoading, setGapsLoading] = useState(false);
  const [gapsError, setGapsError] = useState(null);

  const fetchMonitoringData = async () => {
    if (!userInfo || (userInfo.role !== 'ProviderAdmin' && userInfo.role !== 'MunicipalityAdmin')) return;
    setStatsLoading(true);
    setStatsError(null);
    try {
      const muniParam = userInfo?.role === 'ProviderAdmin' && selectedMunicipalityId ? { municipality_id: selectedMunicipalityId } : {};
      const [statsRes, intentRes, recentRes, gapsRes] = await Promise.all([
        apiClient.get('/stats', { params: { ...muniParam } }),
        apiClient.get('/analytics/intent_breakdown', { params: { days: 7, ...muniParam } }),
        apiClient.get('/analytics/recent', { params: { limit: 50, ...muniParam } }).catch(() => ({ data: [] })),
        apiClient.get('/analytics/gaps', { params: { days: 7, limit: 50, ...muniParam } }).catch((e) => ({ data: [], error: e }))
      ]);
      setStats(statsRes.data);
      setIntentBreakdown(intentRes.data || []);
      setIntentBreakdownError(null);
      setRecentLogs(recentRes.data || []);
      if (gapsRes.error) {
        setGapsList([]);
        setGapsError(gapsRes.error?.response?.data?.detail || gapsRes.error?.message || 'Fehler beim Laden');
      } else {
        setGapsList(gapsRes.data || []);
        setGapsError(null);
      }
    } catch (e) {
      setStatsError(e.response?.data?.detail || e.message || 'Fehler beim Laden');
      setIntentBreakdownError(e.response?.data?.detail || e.message);
    } finally {
      setStatsLoading(false);
      setIntentBreakdownLoading(false);
      setRecentLogsLoading(false);
    }
  };

  useEffect(() => {
    if (userInfo && (userInfo.role === 'ProviderAdmin' || userInfo.role === 'MunicipalityAdmin')) {
      setIntentBreakdownLoading(true);
      setRecentLogsLoading(true);
      fetchMonitoringData();
    }
  }, [userInfo?.role, selectedMunicipalityId]);

  return (
    <>
      <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Monitoring</h2>
      {statsLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Lade KPIs…
        </div>
      )}
      {statsError && (
        <Alert className={isDark ? 'border-slate-700 bg-slate-900' : 'border-red-200 bg-red-50'}>
          <AlertDescription>{statsError}</AlertDescription>
        </Alert>
      )}
      {!statsLoading && !statsError && stats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className={isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Fragen heute</CardTitle>
              </CardHeader>
              <CardContent>
                <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{stats.total_questions_today ?? 0}</span>
              </CardContent>
            </Card>
            <Card className={isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Fragen 7 Tage</CardTitle>
              </CardHeader>
              <CardContent>
                <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{stats.total_questions_week ?? 0}</span>
              </CardContent>
            </Card>
            <Card className={isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Dokumente aktiv / gesamt</CardTitle>
              </CardHeader>
              <CardContent>
                <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{stats.active_documents ?? 0} / {stats.total_documents ?? 0}</span>
              </CardContent>
            </Card>
          </div>
          <div>
            <h3 className={`text-sm font-semibold mb-2 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>Intent-Verteilung (7 Tage)</h3>
            {intentBreakdownLoading ? (
              <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>
            ) : intentBreakdownError ? (
              <p className="text-red-500">{intentBreakdownError}</p>
            ) : (
              <Table>
                <TableHeader className={isDark ? 'bg-slate-950' : 'bg-slate-50'}>
                  <TableRow className={isDark ? 'border-slate-800' : 'border-slate-200'}>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Intent</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Anzahl</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {intentBreakdown.map((row, i) => (
                    <TableRow key={i} className={isDark ? 'border-slate-800' : 'border-slate-200'}>
                      <TableCell className={isDark ? 'text-slate-300' : 'text-slate-700'}>{row.intent}</TableCell>
                      <TableCell className={isDark ? 'text-slate-300' : 'text-slate-700'}>{row.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          {recentLogs.length > 0 && (
            <div>
              <h3 className={`text-sm font-semibold mb-2 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>Letzte Fragen</h3>
              <Table>
                <TableHeader className={isDark ? 'bg-slate-950' : 'bg-slate-50'}>
                  <TableRow className={isDark ? 'border-slate-800' : 'border-slate-200'}>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Zeit</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Intent</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Latenz (ms)</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Frage (redacted)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentLogs.slice(0, 20).map((row, i) => (
                    <TableRow key={i} className={isDark ? 'border-slate-800' : 'border-slate-200'}>
                      <TableCell className={isDark ? 'text-slate-300' : 'text-slate-700'}>{row.timestamp ? new Date(row.timestamp).toLocaleString('de-DE') : '—'}</TableCell>
                      <TableCell className={isDark ? 'text-slate-300' : 'text-slate-700'}>{row.intent ?? '—'}</TableCell>
                      <TableCell className={isDark ? 'text-slate-300' : 'text-slate-700'}>{row.latency_ms ?? '—'}</TableCell>
                      <TableCell className={isDark ? 'text-slate-300 text-xs max-w-[200px] truncate' : 'text-slate-700 text-xs max-w-[200px] truncate'}>{row.question_redacted ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {/* Top Lücken: queries with no citations / low confidence / fallback (last 7 days) */}
          <div>
            <h3 className={`text-sm font-semibold mb-2 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>Top Lücken (7 Tage)</h3>
            {statsLoading ? (
              <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>
            ) : gapsError ? (
              <p className="text-red-500">{gapsError}</p>
            ) : gapsList.length === 0 ? (
              <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>Keine Lücken gefunden.</p>
            ) : (
              <Table>
                <TableHeader className={isDark ? 'bg-slate-950' : 'bg-slate-50'}>
                  <TableRow className={isDark ? 'border-slate-800' : 'border-slate-200'}>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Frage (redacted)</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Anzahl</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Zuletzt</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Intent</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Ø Confidence</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-600'}>Aktion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gapsList.map((row, i) => (
                    <TableRow key={i} className={isDark ? 'border-slate-800' : 'border-slate-200'}>
                      <TableCell className={`text-xs max-w-[220px] truncate ${isDark ? 'text-slate-300' : 'text-slate-700'}`} title={row.question_redacted}>{row.question_redacted ?? '—'}</TableCell>
                      <TableCell className={isDark ? 'text-slate-300' : 'text-slate-700'}>{row.count}</TableCell>
                      <TableCell className={isDark ? 'text-slate-300' : 'text-slate-700'}>{row.last_seen ? new Date(row.last_seen).toLocaleString('de-DE') : '—'}</TableCell>
                      <TableCell className={isDark ? 'text-slate-300' : 'text-slate-700'}>{row.intent ?? '—'}</TableCell>
                      <TableCell className={isDark ? 'text-slate-300' : 'text-slate-700'}>{row.avg_confidence ?? '—'}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            if (onOpenInTestumgebung) {
                              onOpenInTestumgebung(row.question_redacted ?? '');
                            }
                          }}
                        >
                          In Testumgebung öffnen
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      )}
    </>
  );
}
